import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import EditableInfoRow from "./EditableInfoRow";

const CONTACT_TYPES = [
    { id: "EMERGENCY", name: "Emergency" },
    { id: "BILLING", name: "Billing" },
    { id: "SUPPORT", name: "Support" }
];

const DEFAULT_NEW_CONTACT_TYPE = "EMERGENCY";

const ContactInformation = ({
    contacts = [],
    isEditing,
    isLoading,
    onEdit,
    onSave,
    onCancel,
    onChange,
    onAddContact,
    onRemoveContact,
    onSetPrimary
}) => {
    const [collapsed, setCollapsed] = useState(true);
    const [expandedIndex, setExpandedIndex] = useState(0);

    // Find the index of the primary contact
    const primaryIndex = contacts.findIndex(contact => contact.is_primary === 1);

    const handleAddClick = () => {
        onAddContact(DEFAULT_NEW_CONTACT_TYPE);
        setExpandedIndex(contacts.length);
    };

    const handleFieldChange = (index, field, value) => {
        onChange(index, field, value);
    };

    const handleSetPrimary = (index) => {
        if (onSetPrimary) {
            onSetPrimary(index);
        }
    };

    // Check if this contact is the primary one
    const isPrimaryContact = (index) => {
        if (isEditing) {
            return contacts[index]?.is_primary === 1;
        }
        return primaryIndex >= 0 ? index === primaryIndex : index === 0;
    };

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check if there are any validation errors
    const hasValidationErrors = contacts.some(contact => {
        const phone = contact.phone || "";
        const mobile = contact.mobile || "";
        const email = contact.email || "";

        // Phone validation (if provided, must be 10 digits)
        const hasPhoneError = phone.length > 0 && phone.length < 10;
        // Mobile validation (if provided, must be 10 digits)
        const hasMobileError = mobile.length > 0 && mobile.length < 10;
        // Email validation (if provided, must be valid format)
        const hasEmailError = email.length > 0 && !emailRegex.test(email);

        return hasPhoneError || hasMobileError || hasEmailError;
    });

    return (
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-slate-100 tw-mb-4">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-slate-100 tw-bg-gradient-to-r tw-from-slate-50 tw-to-white">
                <div
                    className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none"
                    onClick={() => !isEditing && setCollapsed(prev => !prev)}
                >
                    <Icon
                        icon={collapsed && !isEditing ? "solar:alt-arrow-right-bold" : "solar:alt-arrow-down-bold"}
                        className="tw-text-slate-400 tw-text-sm"
                    />
                    <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="solar:phone-calling-bold" className="tw-text-amber-600 tw-text-lg" />
                    </div>
                    <h3 className="tw-text-sm tw-font-semibold tw-text-slate-700">Contact Information</h3>
                    <span className="tw-text-xs tw-text-slate-400 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
                    </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={onCancel}
                                disabled={isLoading}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-600 tw-bg-slate-100 tw-rounded-lg hover:tw-bg-slate-200 tw-transition-colors disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSave}
                                disabled={isLoading || hasValidationErrors}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-amber-500 tw-rounded-lg hover:tw-bg-amber-600 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-1"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="tw-w-3 tw-h-3 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="solar:check-circle-bold" className="tw-text-sm" />
                                        Save
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onEdit}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-amber-600 tw-bg-amber-50 tw-rounded-lg hover:tw-bg-amber-100 tw-transition-colors tw-flex tw-items-center tw-gap-1"
                        >
                            <Icon icon="solar:pen-bold" className="tw-text-sm" />
                            Edit
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            {(!collapsed || isEditing) && <div className="tw-p-5">
                {contacts.map((contact, index) => (
                    <div
                        key={contact.contact_id ? `contact-${contact.contact_id}` : `new-${index}`}
                        className={`tw-border tw-rounded-lg tw-mb-3 ${
                            expandedIndex === index ? "tw-border-amber-200" : "tw-border-slate-200"
                        }`}
                    >
                        {/* Contact Header */}
                        <div
                            className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-cursor-pointer tw-transition-colors ${
                                expandedIndex === index
                                    ? "tw-bg-amber-50"
                                    : "tw-bg-slate-50 hover:tw-bg-slate-100"
                            }`}
                            onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                        >
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Icon
                                    icon={expandedIndex === index ? "solar:alt-arrow-down-bold" : "solar:alt-arrow-right-bold"}
                                    className="tw-text-slate-400"
                                />
                                <span className="tw-text-sm tw-font-medium tw-text-slate-700">
                                    {contact.contact_person_name || contact.contactPersonName || "Contact"}
                                    {(contact.designation) && (
                                        <span className="tw-text-slate-400 tw-font-normal"> - {contact.designation}</span>
                                    )}
                                </span>
                                {isPrimaryContact(index) && (
                                    <span className="tw-text-xs tw-bg-green-100 tw-text-green-700 tw-px-2 tw-py-0.5 tw-rounded-full">
                                        Primary
                                    </span>
                                )}
                                <span className="tw-text-xs tw-bg-amber-100 tw-text-amber-700 tw-px-2 tw-py-0.5 tw-rounded-full">
                                    {CONTACT_TYPES.find(t => t.id === (contact.contactType || contact.contact_type))?.name || contact.contact_type}
                                </span>
                            </div>
                            {isEditing && contacts.length > 1 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveContact && onRemoveContact(index);
                                    }}
                                    className="tw-p-1 tw-text-red-500 hover:tw-bg-red-50 tw-rounded"
                                >
                                    <Icon icon="solar:trash-bin-trash-bold" className="tw-text-lg" />
                                </button>
                            )}
                        </div>

                        {/* Contact Content */}
                        {expandedIndex === index && (
                            <div className="tw-p-4 tw-bg-white">
                                {/* Primary Checkbox */}
                                {isEditing && (
                                    <div
                                        onClick={() => handleSetPrimary(index)}
                                        className={`tw-mb-4 tw-p-3 tw-rounded-lg tw-border-2 tw-cursor-pointer tw-transition-all tw-flex tw-items-center tw-gap-3 ${
                                            isPrimaryContact(index)
                                                ? "tw-border-green-400 tw-bg-green-50"
                                                : "tw-border-slate-200 tw-bg-slate-50 hover:tw-border-green-300 hover:tw-bg-green-50/50"
                                        }`}
                                    >
                                        <div className={`tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center tw-transition-all ${
                                            isPrimaryContact(index)
                                                ? "tw-border-green-500 tw-bg-green-500"
                                                : "tw-border-slate-300 tw-bg-white"
                                        }`}>
                                            {isPrimaryContact(index) && (
                                                <Icon icon="solar:check-read-bold" className="tw-text-white tw-text-xs" />
                                            )}
                                        </div>
                                        <div className="tw-flex tw-flex-col">
                                            <span className={`tw-text-sm tw-font-medium ${
                                                isPrimaryContact(index) ? "tw-text-green-700" : "tw-text-slate-600"
                                            }`}>
                                                {isPrimaryContact(index) ? "Primary Contact" : "Set as Primary Contact"}
                                            </span>
                                            <span className="tw-text-xs tw-text-slate-400">
                                                {isPrimaryContact(index)
                                                    ? "This contact is used as the default"
                                                    : "Click to make this the default contact"}
                                            </span>
                                        </div>
                                        {isPrimaryContact(index) && (
                                            <Icon icon="solar:verified-check-bold" className="tw-text-green-500 tw-text-xl tw-ml-auto" />
                                        )}
                                    </div>
                                )}
                                <div className="row">
                                    <EditableInfoRow
                                        label="Contact Type"
                                        value={contact.contactType || contact.contact_type}
                                        displayValue={CONTACT_TYPES.find(t => t.id === (contact.contactType || contact.contact_type))?.name}
                                        icon="solar:tag-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="contactType"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        type="select"
                                        options={CONTACT_TYPES}
                                        optionLabel="name"
                                        optionValue="id"
                                    />
                                    <EditableInfoRow
                                        label="Contact Person"
                                        value={contact.contact_person_name || contact.contactPersonName}
                                        icon="solar:user-bold-duotone"
                                        colSize="col-md-5"
                                        isEditing={isEditing}
                                        fieldKey="contactPersonName"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                    />
                                    <EditableInfoRow
                                        label="Designation"
                                        value={contact.designation}
                                        icon="solar:case-bold-duotone"
                                        colSize="col-md-4"
                                        isEditing={isEditing}
                                        fieldKey="designation"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                    />
                                    <EditableInfoRow
                                        label="Phone"
                                        value={contact.phone}
                                        icon="solar:phone-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="phone"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        maxLength={10}
                                        minLength={10}
                                        errorMessage="Phone must be 10 digits"
                                    />
                                    <EditableInfoRow
                                        label="Mobile"
                                        value={contact.mobile}
                                        icon="solar:smartphone-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="mobile"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        maxLength={10}
                                        minLength={10}
                                        errorMessage="Mobile must be 10 digits"
                                    />
                                    <EditableInfoRow
                                        label="Email"
                                        value={contact.email}
                                        icon="solar:letter-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="email"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        type="email"
                                        validateEmail={true}
                                        errorMessage="Please enter a valid email"
                                    />
                                    <EditableInfoRow
                                        label="Website"
                                        value={contact.website}
                                        icon="solar:global-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="website"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Add Contact Button */}
                {isEditing && (
                    <button
                        onClick={handleAddClick}
                        className="tw-w-full tw-py-2.5 tw-border-2 tw-border-dashed tw-border-slate-300 tw-rounded-lg tw-text-slate-500 hover:tw-border-amber-400 hover:tw-text-amber-600 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2"
                    >
                        <Icon icon="solar:add-circle-bold" className="tw-text-lg" />
                        Add New Contact
                    </button>
                )}
            </div>}
        </div>
    );
};

export default ContactInformation;
